import { useState } from 'react';
import { updateHipaaConsent } from '@/apis/users';
import HippaInfo from '@/assets/member-assets/homepage/hippa_info.svg';
import { logger } from '@/lib/logger';
import { showSuccessToast, showErrorToast } from '../ui/CustomToast';

interface HipaaConsentModalProps {
  clientName: string;
  clientId: string;
  accessToken: string;
  onConsentComplete: () => void;
  onClose: () => void;
}

export const HipaaConsentModal = ({
  clientName,
  clientId,
  accessToken,
  onConsentComplete,
  onClose,
}: HipaaConsentModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const currentDate = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const handleConsent = async () => {
    try {
      setIsLoading(true);

      await updateHipaaConsent(
        clientId,
        {
          hipaa_consent: true,
          hipaa_consent_date: new Date().toISOString(),
          hipaa_consent_version: '1.0',
        },
        accessToken
      );
      showSuccessToast('Consent recorded', 'Your authorization has been saved securely.');
      onConsentComplete();
    } catch (error) {
      logger.error('Error updating HIPAA consent:', error);
      showErrorToast('Something went wrong', "We couldn't save your consent. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 font-sans"
      data-testid="hipaaConsent_modalOverlay"
    >
      <div
        className="bg-white rounded-[24px] max-w-xl w-full max-h-[90vh] relative shadow-2xl overflow-hidden flex flex-col"
        data-testid="hipaaConsent_modal"
      >
        {/* Close Button */}
        {/* <button
          onClick={onClose}
          className="absolute top-5 right-5 text-gray-400 hover:text-gray-700 transition-colors z-10"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button> */}

        {/* Header (non-scrollable) */}
        <div className="pt-8 ps-8 pe-8 pb-2 bg-white" data-testid="hipaaConsent_modalHeader">
          <div className="w-10 h-10 bg-[#E7F8F0] rounded-full flex items-center justify-center mb-4">
            <img src={HippaInfo} alt="HIPAA Information Icon" />
          </div>
          <h2 className="text-xl font-semibold text-[#1C1C1E] mt-4 leading-tight">
            HIPAA Authorization for Use and Disclosure of Protected Health Information (PHI)
          </h2>
          <div className="mt-8 flex justify-between text-sm text-gray-800">
            <p className="flex-1">
              <span className="font-semibold text-gray-900">Client name:</span>{' '}
              <span className="text-gray-700">{clientName}</span>
            </p>
            <p className="text-right">
              <span className="font-semibold text-gray-900">Date:</span>{' '}
              <span className="text-gray-700">{currentDate}</span>
            </p>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div
          className="ms-8 me-8 overflow-y-auto flex-1 border-2 border-gray-200 rounded-lg"
          data-testid="hipaaConsent_modalBody"
        >
          <div className=" text-gray-700 custom-scrollbar text-justify p-3">
            <p className="text-sm mb-3">
              I, <span className="font-bold text-gray-900">{clientName}</span>, hereby authorize PMX
              Health ("Provider") to use and disclose my protected health information ("PHI") in
              accordance with this authorization and as permitted under the Health Insurance
              Portability and Accountability Act of 1996 ("HIPAA") and its implementing regulations.
            </p>
            <div className="space-y-4">
              {/* Section 1 */}
              <div>
                <p className="font-semibold text-sm text-gray-900">
                  1. Information to be Used/Disclosed:
                </p>
                <div className="pl-4 mt-2 space-y-2 text-sm">
                  <p>PMX Health may use and disclose the following health information:</p>
                  <ul className="pl-4 list-disc space-y-1 marker:text-gray-500">
                    <li>Complete health record</li>
                    <li>Diagnoses and treatment information</li>
                    <li>Laboratory test results and analysis</li>
                    <li>Health assessment results</li>
                    <li>Medical history and examination records</li>
                    <li>Treatment plans and progress notes</li>
                    <li>Consultation reports and referrals</li>
                  </ul>
                </div>
              </div>
              {/* Section 2 */}
              <div>
                <p className="font-semibold text-sm text-gray-900">2. Purpose of Disclosure:</p>
                <p className="text-sm pl-4 mt-2">
                  This information will be used for providing comprehensive healthcare services,
                  managing your appointments with PMX Health, coordinating your care between
                  healthcare providers, and creating personalized health assessments and treatment
                  plans to support your overall health and wellness goals.
                </p>
              </div>
              {/* Section 3 */}
              <div>
                <p className="font-semibold text-sm text-gray-900">
                  3. Recipient(s) of Information:
                </p>
                <p className="text-sm pl-4 mt-2">
                  PMX Health authorized healthcare providers, clinical staff, laboratory partners,
                  specialists, and designated third-party service providers directly involved in
                  your healthcare delivery and treatment.
                </p>
              </div>
              {/* Section 4 */}
              <div>
                <p className="font-semibold text-sm text-gray-900">4. Expiration:</p>
                <p className="text-sm pl-4 mt-2">
                  This authorization will remain in effect until explicitly revoked by you or when
                  your relationship with PMX Health is terminated, whichever occurs first.
                </p>
              </div>
              {/* Section 5 */}
              <div>
                <p className="font-semibold text-sm text-gray-900">5. Your Rights:</p>
                <ul className="text-sm pl-8 list-disc space-y-1 marker:text-gray-500 mt-2">
                  <li>
                    You may revoke this authorization at any time by notifying PMX Health in writing
                    at help@pmxhealth.com.
                  </li>
                  <li>Revocation will not affect information already disclosed.</li>
                  <li>
                    Your treatment or eligibility for healthcare services will not be conditioned on
                    whether you sign this authorization.
                  </li>
                  <li>
                    Information disclosed pursuant to this authorization may be subject to
                    redisclosure by the recipient and no longer protected by federal privacy
                    regulations.
                  </li>
                  <li>
                    You have the right to request access to your health information maintained by
                    PMX Health and to request restrictions on certain uses and disclosures.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        {/* Footer (sticky) */}
        <div className="p-8 pt-6 bg-white sticky bottom-0" data-testid="hipaaConsent_modalFooter">
          <p className="text-sm text-gray-600 mb-4">
            <span className="font-semibold text-gray-900">Acknowledgment:</span> By confirming or
            signing below, I acknowledge that I have read and understand this authorization and
            agree to the use and disclosure of my health information as described above.
          </p>
          <button
            onClick={handleConsent}
            disabled={isLoading}
            data-testid="hipaaConsent_consentBtn"
            className="py-3 px-6 w-full rounded-full text-base font-medium bg-[#1C1C1E] text-white hover:bg-black transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving your consent…' : 'I agree and consent'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HipaaConsentModal;
